import { ComputedRef, computed, reactive } from 'vue'
import { Note, INote } from '@/common/classes'
import { ParcelType } from '@/constants'
import { APIResponse, ExtendedStoreResponse, StoreResponse } from '@/common/interfaces'

import useApiService from '@/composables/api'
const apiService = useApiService()

interface State {
  notes: Map<string, Note>
  notesToCopy: Note[]
}

const state: State = reactive({
  notes: new Map(),
  notesToCopy: [],
})

interface Actions {
  getNotes: (wallId: string) => Promise<StoreResponse>
  getNotesForExternalDuplication: (wallId: string) => Promise<ExtendedStoreResponse>
  stageNoteCopy: (note: Note) => void
  clearStagedCopies: () => void
  clearNotes: () => void
  addNewNote: (note: Note, externalDuplicate?: boolean) => Promise<ExtendedStoreResponse>
  updateNote: (note: Note, wallId: string, parcelType: ParcelType) => Promise<StoreResponse>
  localCreate: (note: Note) => void
  localUpdate: (note: Note) => void
  localDelete: (note: Note) => void
}

interface Getters {
  sortedNotes: ComputedRef<Note[]>
  notesToCopy: ComputedRef<Note[]>
}

interface ServiceInterface {
  state: State
  actions: Actions
  getters: Getters
}

function useNoteStore(): ServiceInterface {
  const actions = {
    async getNotes(wallId: string): Promise<StoreResponse> {
      state.notes.clear()
      const res = await apiService.request.fetchNotes(wallId)
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Notes could not be fetched or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        res.data.forEach((n) => {
          const note = new Note(n)
          state.notes.set(note._id, note)
        })
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async getNotesForExternalDuplication(wallId: string): Promise<ExtendedStoreResponse> {
      const res = await apiService.request.fetchNotes(wallId)
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Notes could not be fetched or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        const notes = new Map()
        res.data.forEach((n) => {
          const note = new Note(n)
          notes.set(note._id, note)
        })
        return { success: true, body: notes }
      }
      return { success: false, error: res.errors }
    },

    stageNoteCopy(note: Note): void {
      const index = state.notesToCopy.findIndex((n) => n._id === note._id)
      if (index === -1) state.notesToCopy.push(note)
      else state.notesToCopy[index] = note
    },

    clearStagedCopies(): void {
      state.notesToCopy = []
    },

    clearNotes(): void {
      state.notes.clear()
      state.notesToCopy = []
    },

    async addNewNote(note: Note, externalDuplicate = false): Promise<ExtendedStoreResponse> {
      const res = await apiService.request.createNote(note.wall_id, note)
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Note could not be created or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        const n = new Note(res.data)
        if (!externalDuplicate) state.notes.set(n._id, n) // don't add the note to the store
        return { success: true, body: n }
      }
      return { success: false, error: res.errors }
    },

    async updateNote(note: Note, wallId: string, parcelType: ParcelType): Promise<StoreResponse> {
      const res = await apiService.request.updateNote(wallId, note._id, parcelType, note)
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Note could not be updated or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        const updatedNote = new Note(res.data)
        const oldNote = state.notes.get(note._id)
        if (oldNote) oldNote.update(updatedNote)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    /*  ---------- Local tasks -----------
     *  Called from an incoming Parcel, or from the 1st stage methods above
     *  Local updates only, these methods do NOT update the database
     */

    // Put the message into the store, scan it for hashtags, configure filters
    localCreate(note: Note): void {
      state.notes.set(note._id, note)
    },

    localUpdate(note: Note): void {
      state.notes.set(note._id, note)
    },

    localDelete(note: Note): void {
      state.notes.delete(note._id)
    },
  }

  const getters = {
    get sortedNotes(): ComputedRef<Note[]> {
      return computed(() => {
        const n = Array.from(state.notes.values())
        n.sort((c: Note, n: Note) => {
          return c.status.created > n.status.created ? -1 : 1
        })
        return n
      })
    },

    get notesToCopy(): ComputedRef<Note[]> {
      return computed(() => {
        return state.notesToCopy
      })
    },
  }

  return { state, actions, getters }
}

export { useNoteStore }
